/**
 * /pipeline-facility/welds — the weld register (GET /pipeline-facility/welds,
 * fieldiq-api#1219, PF-3a). A weld is recorded on its job (job → Welds → New
 * weld, or from a dig repair), so this register has no New button of its own.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Hammer, Search } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfWelds } from '../../../hooks/usePfDigs';
import { queryLoadFailure } from '../../../lib/load-failure';
import { fmtNumber, pfDay } from '../../../lib/pipeline-facility';
import { pfWeldResultSkin } from '../../../lib/pf-digs';
import type { PfWeld } from '../../../types/pf-digs';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_EYEBROW, PfBadge, PfEmpty, PfPanel, PfRegister, type PfColumn } from '../PfBits';

export function weldColumns(chainageUnit: string | null | undefined): PfColumn<PfWeld>[] {
  return [
    {
      key: 'weld',
      header: 'Weld',
      render: (w) => (
        <>
          <p className="font-mono font-extrabold text-white">{w.weldNumber || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{w.jobNumber || '—'}</p>
        </>
      ),
    },
    { key: 'dig', header: 'Dig', render: (w) => <span className="font-mono">{w.digNumber || '—'}</span> },
    { key: 'chainage', header: `Chainage${chainageUnit ? ` (${chainageUnit})` : ''}`, align: 'right', render: (w) => <span className="font-mono">{fmtNumber(w.chainage)}</span> },
    { key: 'welder', header: 'Welder', render: (w) => (<><p>{w.welderName || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{w.welderStamp || '—'}</p></>) },
    { key: 'date', header: 'Welded', render: (w) => pfDay(w.weldDate) },
    { key: 'ndt', header: 'NDT company', render: (w) => w.ndtCompany || '—' },
    { key: 'report', header: 'NDT report', render: (w) => (w.ndtReportFileKey ? 'Attached' : w.ndtReportRef || '—') },
    { key: 'links', header: 'NDT links', align: 'right', render: (w) => displayValue(w.ndtLinks?.length ?? undefined) },
    { key: 'result', header: 'Result', render: (w) => <PfBadge skin={pfWeldResultSkin(w.ndtResult)} value={w.ndtResult} /> },
  ];
}

export default function PfWeldsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) {
        const p = new URLSearchParams(searchParams);
        if (search.trim()) p.set('q', search.trim());
        else p.delete('q');
        setSearchParams(p, { replace: true });
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const filters = useMemo(() => ({ q: qParam }), [qParam]);
  const listQuery = usePfWelds(filters);
  const allQuery = usePfWelds();
  const welds = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? null;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (w: PfWeld) => boolean) => displayValue(all ? all.filter(pick).length : undefined);

  return (
    <div data-testid="pf-welds-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Welds"
        description="Repair and tie-in welds, who made them, and their NDT."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <Hammer className="size-4" aria-hidden="true" />, label: 'Welds', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <Hammer className="size-4" aria-hidden="true" />, label: 'NDT pending', tone: 'accent', loading: allQuery.isLoading, value: countOf((w) => !w.ndtResult || w.ndtResult === 'pending') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            A weld is recorded on its <Link className="underline" to="/pipeline-facility/jobs">job</Link> (Welds → New weld), or from a dig repair.
          </p>
          <label className="relative block">
            <span className="sr-only">Search weld, welder, heat number or job</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search weld, welder, heat number or job"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>
          <PfPanel eyebrow="Weld register" title="Welds" testId="pf-weld-register" footer={!listQuery.isLoading && welds.length ? `${welds.length} weld${welds.length === 1 ? '' : 's'}` : undefined}>
            <PfRegister
              query={listQuery}
              rows={welds}
              columns={weldColumns(units?.chainage)}
              rail={(w) => pfWeldResultSkin(w.ndtResult).rail}
              onOpen={(w) => openRecord(`/pipeline-facility/welds/${w.id}`)}
              subject="welds"
              testId="pf-weld"
              minWidth="min-w-[1100px]"
              empty={qParam ? <PfEmpty>No weld matches this search.</PfEmpty> : <PfEmpty>No welds recorded yet.</PfEmpty>}
              card={(w) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-extrabold text-white">{w.weldNumber}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">{w.jobNumber || '—'} · {w.digNumber || 'No dig'}</p>
                    </div>
                    <PfBadge skin={pfWeldResultSkin(w.ndtResult)} value={w.ndtResult} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">{w.welderName || '—'} · {pfDay(w.weldDate)} · {w.ndtCompany || '—'}</p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
